import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTarget, FiMap, FiUsers, FiTrendingUp, FiCheckCircle, FiArrowRight } = FiIcons;

const StrategicPlanning = () => {
  const keyDeliverables = [
    {
      icon: FiMap,
      title: 'Competitive Landscape Analysis',
      description: 'Comprehensive assessment of market dynamics, competitor positioning, and emerging opportunities within the South African context.'
    },
    {
      icon: FiTarget,
      title: 'Regulatory Framework Assessment',
      description: 'Detailed evaluation of regulatory requirements, compliance obligations, and potential regulatory changes affecting your industry.'
    },
    {
      icon: FiUsers,
      title: 'Stakeholder Mapping',
      description: 'Identification and analysis of key stakeholders, including government entities, community groups, and business partners.'
    },
    {
      icon: FiTrendingUp,
      title: 'Implementation Timelines',
      description: 'Detailed project timelines with milestones, resource requirements, and risk mitigation strategies for successful execution.'
    }
  ];

  const marketEntryServices = [
    'Market feasibility studies',
    'Local partnership identification',
    'Regulatory compliance roadmaps',
    'BEE strategy development',
    'Community engagement plans',
    'Risk assessment and mitigation',
    'Go-to-market strategy',
    'Local talent acquisition planning'
  ];

  const expansionServices = [
    'Regional market analysis',
    'Growth strategy development',
    'Resource allocation planning',
    'Operational scaling strategies',
    'International expansion roadmaps',
    'Joint venture structuring',
    'Market positioning strategies',
    'Performance metrics framework'
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-purple-900 to-purple-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <h1 className="text-4xl lg:text-5xl font-bold mb-6 font-serif">
              Strategic Planning & Market Entry
            </h1>
            <p className="text-xl text-gray-200 leading-relaxed mb-8">
              Our strategic planning engagements deliver comprehensive roadmaps for sustainable growth within South Africa's dynamic economic environment. We specialise in market entry strategies for international companies establishing local presence, as well as expansion planning for South African enterprises seeking regional or global opportunities.
            </p>
            <Link
              to="/contact"
              className="bg-yellow-400 text-purple-900 px-8 py-4 rounded-lg font-semibold hover:bg-yellow-300 transition-colors inline-flex items-center"
            >
              Discuss Your Strategy
              <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Key Deliverables */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 font-serif">
              Key Deliverables
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our strategic planning process incorporates thorough understanding of local procurement requirements, BEE obligations, and community development imperatives.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {keyDeliverables.map((deliverable, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white border border-gray-200 rounded-xl p-8 hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-6">
                  <SafeIcon icon={deliverable.icon} className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  {deliverable.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {deliverable.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Market Entry vs Expansion */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 font-serif">
              Tailored Approaches
            </h2>
            <p className="text-xl text-gray-600">
              Whether you're entering the South African market or expanding globally, we provide specialized expertise for your unique situation.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Market Entry */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="bg-white rounded-xl p-8 shadow-lg"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6 font-serif">
                Market Entry for International Companies
              </h3>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Comprehensive support for international companies establishing presence in South Africa, navigating regulatory requirements and building local partnerships.
              </p>
              <ul className="space-y-3">
                {marketEntryServices.map((service, index) => (
                  <li key={index} className="flex items-center text-gray-700">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                    {service}
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Expansion */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="bg-white rounded-xl p-8 shadow-lg"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6 font-serif">
                Expansion Planning for Local Enterprises
              </h3>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Strategic guidance for South African companies seeking regional or global growth opportunities, leveraging local strengths for international success.
              </p>
              <ul className="space-y-3">
                {expansionServices.map((service, index) => (
                  <li key={index} className="flex items-center text-gray-700">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                    {service}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process Overview */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 font-serif">
              Our Strategic Planning Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A structured approach that ensures comprehensive analysis and actionable recommendations tailored to South African market conditions.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                phase: 'Phase 1',
                title: 'Market Analysis & Discovery',
                description: 'Comprehensive assessment of market conditions, competitive landscape, and regulatory environment.',
                duration: '2-4 weeks'
              },
              {
                phase: 'Phase 2',
                title: 'Strategy Development',
                description: 'Development of tailored strategic roadmap with clear objectives, timelines, and success metrics.',
                duration: '3-6 weeks'
              },
              {
                phase: 'Phase 3',
                title: 'Implementation Planning',
                description: 'Detailed implementation plan with resource allocation, risk mitigation, and performance monitoring.',
                duration: '2-4 weeks'
              }
            ].map((phase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-purple-600 text-white rounded-full flex items-center justify-center mx-auto mb-6 text-lg font-bold">
                  {index + 1}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{phase.phase}</h3>
                <h4 className="text-lg font-medium text-purple-700 mb-4">{phase.title}</h4>
                <p className="text-gray-600 mb-4 leading-relaxed">{phase.description}</p>
                <span className="text-sm text-gray-500 font-medium">{phase.duration}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Study Preview */}
      <section className="py-20 bg-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 font-serif">
                Success Story: JSE Listing Strategy
              </h2>
              <blockquote className="text-lg text-gray-700 italic mb-6 leading-relaxed">
                "RSA Solutions' strategic guidance was instrumental in our successful JSE listing. Their deep understanding of regulatory requirements combined with practical implementation support made the complex process manageable."
              </blockquote>
              <p className="text-purple-700 font-semibold mb-8">— CEO, Emerging Mining Company</p>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <div className="text-3xl font-bold text-purple-900 mb-2">18 months</div>
                  <div className="text-gray-600">From strategy to listing</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-purple-900 mb-2">R2.5B</div>
                  <div className="text-gray-600">Market capitalisation</div>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <img
                src="https://images.unsplash.com/photo-1559526324-4b87b5e36e44?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="JSE trading floor"
                className="rounded-lg shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-purple-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6 font-serif">
              Ready to Develop Your Strategic Roadmap?
            </h2>
            <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
              Let's discuss how our strategic planning expertise can accelerate your growth and market positioning in South Africa.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-yellow-400 text-purple-900 px-8 py-4 rounded-lg font-semibold hover:bg-yellow-300 transition-colors inline-flex items-center justify-center"
              >
                Schedule Strategic Consultation
                <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
              </Link>
              <Link
                to="/services"
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-purple-900 transition-colors inline-flex items-center justify-center"
              >
                View All Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default StrategicPlanning;